// Ordering helpers for initiatives.
//
// HISTORY: this module once carried drag-to-reorder (midpoint sortOrder math,
// planDrop). That was designed for the quarter-only model where owners had no
// dates and needed a manual rank. Once target dates became required, manual
// ranking was a second, confusing ordering concept — removed 2026-07-31.
// Display order is now chronological; the API's sort_order column is dormant
// (still populated sanely on create so nothing breaks if it's ever revived).

import type { Initiative } from "./types";
import { toDate, normalizeQuarterId } from "./constants";

// Legacy rows may lack sortOrder at runtime.
export function orderOf(i: Initiative): number {
  return i.sortOrder ?? 0;
}

// sortOrder for a NEW initiative: one past the current max in its quarter.
// Purely for data coherence — nothing in the UI reads it anymore.
export function nextSortOrder(initiatives: Initiative[], targetQ: string): number {
  const want = normalizeQuarterId(targetQ);
  let max = -1;
  for (const i of initiatives) {
    if (i.archivedAt || normalizeQuarterId(i.targetQ) !== want) continue;
    const o = orderOf(i);
    if (o > max) max = o;
  }
  return max + 1;
}

// Chronological display order: earliest start first (target stands in when a
// row has no start), then target, then name for a stable tiebreak.
export function byStart(a: Initiative, b: Initiative): number {
  const aStart = toDate(a.start) ?? toDate(a.target);
  const bStart = toDate(b.start) ?? toDate(b.target);
  const at = aStart ? aStart.getTime() : Infinity;
  const bt = bStart ? bStart.getTime() : Infinity;
  if (at !== bt) return at - bt;
  const aTgt = toDate(a.target)?.getTime() ?? Infinity;
  const bTgt = toDate(b.target)?.getTime() ?? Infinity;
  if (aTgt !== bTgt) return aTgt - bTgt;
  return a.name.localeCompare(b.name);
}
